import { useAuth } from "@/providers/auth";
import { useCRM } from "@/providers/crm";
import { DataService } from "@/services/data";
import type { TokensType } from "@/types/auth";
import { DynamicAction } from "@/types/dynamic-detail";
import { ChevronDown, Loader2, MoreHorizontal, Plus } from "lucide-react";
import React, { useCallback, useEffect, useState } from "react";
import { toast } from "sonner";
import { Button } from "../ui/button";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuPortal, DropdownMenuSub, DropdownMenuSubContent, DropdownMenuSubTrigger, DropdownMenuTrigger } from "../ui/dropdown-menu";
import { ActionModal } from "./action-modal";
import { CUSTOM_ACTION_DIALOGS } from "./custom-action-dialogs";
import { OpenFormDialog } from "./open-form-dialog";
import type { FilterTree } from "./filter-tree/types";
import { prepareAction } from "./prepare-action";
import type { ActionModalProps, ActionResolution } from "./types2";

type ActionEntry = { kind: "action"; action: DynamicAction } | { kind: "group"; label: string; actions: DynamicAction[] };

/** Actions in server order, those sharing a `group` folded into one entry at the place of the first. */
function groupActions(actions: DynamicAction[]): ActionEntry[] {
    const entries: ActionEntry[] = [];
    const groups = new Map<string, { kind: "group"; label: string; actions: DynamicAction[] }>();
    for (const action of actions) {
        if (!action.group) {
            entries.push({ kind: "action", action });
            continue;
        }
        let group = groups.get(action.group);
        if (!group) {
            group = { kind: "group", label: action.group, actions: [] };
            groups.set(action.group, group);
            entries.push(group);
        }
        group.actions.push(action);
    }
    return entries;
}

type ObjectBatchActionsProps = React.HTMLAttributes<HTMLDivElement> & {
    actions: DynamicAction[];
    selectedItemIds: string[];
    selectAll: boolean;
    selectAllCount: number;
    endpointUrl: string;
    action_scope: "list" | "detail";
    drqQuery?: string | null;
    /** Search-bar value — forwarded with is_all so actions respect the list search filter */
    searchValue?: string;
    /** Visible column fields required by the backend to apply ?search= */
    searchFields?: string[];
    /** Advanced filters of the list — forwarded with select-all */
    filterTree?: FilterTree | null;
    /** Called after an action completed successfully (e.g. to refresh the list) */
    onActionSuccess?: () => void;
    /**
     * `buttons` is the toolbar of a list or a detail header. `menu` collapses everything into a
     * kebab, for a place where one row is one record. `add` is the related-list header: one
     * Plus button whose menu lists the writes. `inline` is a section header: small text buttons. `standalone` is
     * one plain button per action, without the toolbar frame (an alert's call to action).
     */
    layout?: "buttons" | "menu" | "add" | "inline" | "standalone";
    /** A shorter label per action name, where the place says the rest (« Modifier » on « Convention »). */
    labels?: Record<string, string>;
};

export function Actions2({
    actions,
    selectedItemIds,
    selectAll,
    selectAllCount,
    endpointUrl,
    action_scope,
    drqQuery,
    searchValue,
    searchFields,
    filterTree,
    onActionSuccess,
    layout = "buttons",
    labels
}: ObjectBatchActionsProps) {
    const [openActionModal, setOpenActionModal] = useState(false);
    const [selectedAction, setSelectedAction] = useState<ActionModalProps | null>(null);
    const [preparingActionName, setPreparingActionName] = useState<string | null>(null);
    const [customActionName, setCustomActionName] = useState<string | null>(null);
    const [customDialogOpen, setCustomDialogOpen] = useState(false);
    // `open_form`: another object's creation form, opened in a dialog on this record.
    const [openFormAction, setOpenFormAction] = useState<DynamicAction | null>(null);
    const { clearCache } = useCRM();
    const { activateViewAs } = useAuth();

    /** `values`: what a refusal carried over (`ActionResolution`), over the action's own defaults. */
    const openAction = async (action: DynamicAction, values?: Record<string, unknown>) => {
        if (action.open_form) {
            setOpenFormAction(action);
            return;
        }
        if (CUSTOM_ACTION_DIALOGS[action.action_name]) {
            // Custom dialogs need a concrete record id — selectAll alone is not enough.
            if (selectedItemIds[0] == null) {
                toast.error("Select a record to run this action.");
                return;
            }
            setCustomActionName(action.action_name);
            setCustomDialogOpen(true);
            return;
        }

        setPreparingActionName(action.action_name);
        let prepared: ActionModalProps | null;
        try {
            prepared = await prepareAction(action, { endpointUrl, action_scope, ids: selectedItemIds, is_all: selectAll, drqQuery, filterTree }, values);
        } finally {
            setPreparingActionName(null);
        }
        if (!prepared) return;
        setSelectedAction(prepared);
        setOpenActionModal(true);
    };

    // A refusal offers another action of the record: it replaces the dialog, with what was typed.
    const openResolution = (resolution: ActionResolution, typed: Record<string, unknown>) => {
        const target = actions.find(action => action.action_name === resolution.action);
        if (!target) {
            toast.error("Cette action n'est pas disponible sur ce dossier.");
            return;
        }
        setOpenActionModal(false);
        void openAction(target, { ...typed, ...resolution.values });
    };

    useEffect(() => {
        return () => {
            if (openActionModal) {
                setSelectedAction(null);
                setOpenActionModal(false);
            }
        };
    }, [openActionModal]);

    const CustomActionDialog = customActionName ? CUSTOM_ACTION_DIALOGS[customActionName] : null;
    const handleCustomDialogOpenChange = useCallback((nextOpen: boolean) => {
        setCustomDialogOpen(nextOpen);
        if (!nextOpen) {
            setCustomActionName(null);
        }
    }, []);

    const runAction = async (action: DynamicAction) => {
        if (action.action_name === "view_as") {
            try {
                const data = await DataService.client.post({}, `${endpointUrl}${selectedItemIds[0]}/view_as/`).json<{ tokens?: TokensType }>();
                const tokens = data.tokens;
                if (tokens) {
                    activateViewAs(tokens);
                    clearCache();
                    toast.success("You are now navigating as user");
                } else {
                    toast.error("Token view as missing in the response");
                }
            } catch (e) {
                toast.error("Impossible to activate the view as mode");
            }
            return;
        }
        await openAction(action);
    };

    const selectedCount = selectAll ? selectAllCount : selectedItemIds.length;
    const exceedsMaxRecords = (action: DynamicAction) => action.max_records != null && selectedCount > action.max_records;

    const isDisabled = (action: DynamicAction) => (action.need_selection && selectedItemIds.length === 0 && !selectAll) || exceedsMaxRecords(action) || preparingActionName !== null;

    const actionLabel = (action: DynamicAction) => (
        <>
            {labels?.[action.action_name] ?? action.title}
            {exceedsMaxRecords(action) && <span className="ml-1 text-xs text-muted-foreground">(select at most {action.max_records})</span>}
        </>
    );

    const dialogs = (
        <>
            {selectedAction && selectedAction.fields && (
                <ActionModal
                    open={openActionModal}
                    onOpenChange={setOpenActionModal}
                    action_title={selectedAction.action_title}
                    action_description={selectedAction.action_description}
                    action_api_name={selectedAction.action_api_name}
                    redirect_url={selectedAction.redirect_url}
                    redirect_target={selectedAction.redirect_target}
                    button_label={selectedAction.button_label}
                    fields={selectedAction.fields}
                    records={selectedItemIds}
                    endpointUrl={endpointUrl}
                    action_scope={action_scope}
                    sync={selectedAction.sync}
                    checks={selectedAction.checks}
                    is_all={selectAll}
                    drqQuery={drqQuery}
                    searchValue={searchValue}
                    searchFields={searchFields}
                    filterTree={filterTree}
                    onSuccess={onActionSuccess}
                    onResolution={openResolution}
                    preflight_context={selectedAction.preflight_context}
                />
            )}
            {openFormAction?.open_form && (
                <OpenFormDialog
                    open
                    onOpenChange={next => {
                        if (!next) setOpenFormAction(null);
                    }}
                    title={openFormAction.title}
                    descriptor={openFormAction.open_form}
                    onSuccess={onActionSuccess}
                />
            )}
            {CustomActionDialog && selectedItemIds[0] != null && (
                <CustomActionDialog recordId={selectedItemIds[0]} open={customDialogOpen} onOpenChange={handleCustomDialogOpenChange} onSuccess={onActionSuccess} />
            )}
        </>
    );

    // An action offered only by a refusal stays in `actions` (`openResolution` finds it there), out of the menu.
    const entries = groupActions(actions.filter(action => action.in_menu !== false));

    const menuItem = (action: DynamicAction, index: number) => (
        <DropdownMenuItem
            key={`${action.action_name}_${index}`}
            onSelect={() => {
                void runAction(action);
            }}
            disabled={isDisabled(action)}
        >
            {action.group && action.menu_label ? action.menu_label : actionLabel(action)}
        </DropdownMenuItem>
    );

    const menuEntries = (list: ActionEntry[]) =>
        list.map((entry, index) =>
            entry.kind === "action" ? (
                menuItem(entry.action, index)
            ) : (
                <DropdownMenuSub key={`group_${entry.label}`}>
                    <DropdownMenuSubTrigger>{entry.label}</DropdownMenuSubTrigger>
                    <DropdownMenuPortal>
                        <DropdownMenuSubContent>{entry.actions.map(menuItem)}</DropdownMenuSubContent>
                    </DropdownMenuPortal>
                </DropdownMenuSub>
            )
        );

    const menuItems = menuEntries(entries);

    if (layout === "menu") {
        return (
            <>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="ghost" size="sm" className="h-7 w-7 p-0" disabled={preparingActionName !== null}>
                            {preparingActionName !== null ? <Loader2 className="h-4 w-4 animate-spin" /> : <MoreHorizontal className="h-4 w-4" />}
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">{menuItems}</DropdownMenuContent>
                </DropdownMenu>
                {dialogs}
            </>
        );
    }

    if (layout === "inline") {
        return (
            <>
                <div className="flex items-center gap-1">
                    {actions.map((action, index) => (
                        <Button
                            key={`${action.action_name}_${index}`}
                            type="button"
                            variant="ghost"
                            size="sm"
                            className="h-6 px-2 text-xs"
                            title={action.title}
                            onClick={() => runAction(action)}
                            disabled={isDisabled(action)}
                        >
                            {preparingActionName === action.action_name && <Loader2 className="mr-1 h-3 w-3 animate-spin" />}
                            {actionLabel(action)}
                        </Button>
                    ))}
                </div>
                {dialogs}
            </>
        );
    }

    if (layout === "standalone") {
        return (
            <>
                <div className="flex flex-wrap items-center gap-2">
                    {actions.map((action, index) => (
                        <Button key={`${action.action_name}_${index}`} type="button" variant="outline" size="sm" title={action.title} onClick={() => runAction(action)} disabled={isDisabled(action)}>
                            {preparingActionName === action.action_name && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                            {actionLabel(action)}
                        </Button>
                    ))}
                </div>
                {dialogs}
            </>
        );
    }

    if (layout === "add") {
        return (
            <>
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button size="sm" variant="outline" disabled={preparingActionName !== null}>
                            {preparingActionName !== null ? <Loader2 className="mr-1 h-4 w-4 animate-spin" /> : <Plus className="mr-1 h-4 w-4" />}
                            Add
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent align="end">{menuItems}</DropdownMenuContent>
                </DropdownMenu>
                {dialogs}
            </>
        );
    }

    return (
        <div className="flex rounded-sm border shadow-sm">
            {entries.slice(0, 3).map((entry: ActionEntry, index: number) =>
                entry.kind === "action" ? (
                    <Button
                        key={index}
                        variant="outline"
                        className="h-full rounded-none border-b-0 border-l-0 border-t-0 shadow-none first:rounded-l-sm last:rounded-r last:border-r-0"
                        onClick={() => runAction(entry.action)}
                        disabled={isDisabled(entry.action)}
                    >
                        {preparingActionName === entry.action.action_name && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        {actionLabel(entry.action)}
                    </Button>
                ) : (
                    <DropdownMenu key={index}>
                        <DropdownMenuTrigger asChild>
                            <Button
                                variant="outline"
                                className="h-full rounded-none border-b-0 border-l-0 border-t-0 shadow-none first:rounded-l-sm last:rounded-r last:border-r-0"
                                disabled={preparingActionName !== null}
                            >
                                {entry.actions.some(action => action.action_name === preparingActionName) && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                {entry.label}
                                <ChevronDown className="ml-1 h-4 w-4" />
                            </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent>{entry.actions.map(menuItem)}</DropdownMenuContent>
                    </DropdownMenu>
                )
            )}
            {entries.length > 3 && (
                <DropdownMenu>
                    <DropdownMenuTrigger asChild>
                        <Button variant="outline" size="icon" className="m-0  rounded-l-none rounded-r border-0 p-0 shadow-none">
                            <ChevronDown className="h-4 w-4" />
                        </Button>
                    </DropdownMenuTrigger>
                    <DropdownMenuContent>{menuEntries(entries.slice(3))}</DropdownMenuContent>
                </DropdownMenu>
            )}
            {dialogs}
        </div>
    );
}
